import { useMemo, useState } from 'react';
import { Button } from '../../components';
import Modal from '../../components/modals/Modal';
import styles from './ReportSelector.module.scss';
import { auditReportTypes } from '../audits';
import { Form } from 'react-bootstrap';
import { Text } from '../../components/typography';
import { downloadPDF } from './misc';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';

export default function ReportSelector({
  inspection,
  correctiveActions,
  notes,
  fileName,
  options = [],
  buttonText = 'Report',
  buttonIcon = 'download',
  variant = 'primary',
  onSubmit = () => {},
  title = 'Report Options',
  isAudit = true,
  subtitle = 'Select Options',
  contentOptions = [],
  showDescription = false,
  copy,
  setCopy = () => {},
  showCopy = false,
}) {
  const [showModal, setShowModal] = useState(false);
  const [props, setProps] = useState({});
  const [selected, setSelected] = useState([]);
  const handleCheckboxChange = (reportType) => {
    setProps((prevSelected) => ({
      ...prevSelected,
      [reportType]: !prevSelected[reportType],
    }));
  };
  const online = useOnlineStatus();
  const otherOptions = [...(options || []), ...(contentOptions || [])];
  const handleSelectAll = () => {
    if (!!otherOptions?.length) {
      allOptionsSelected
        ? setSelected([])
        : otherOptions.forEach((o) => selected.push(o.id));
    }
    const types = isAudit
      ? Object.keys(auditReportTypes)
      : otherOptions.map((o) => o.key || o.id);

    const allOptions = types.reduce((acc, key) => {
      acc[key] = allOptionsSelected ? false : true;
      return acc;
    }, {});
    if (isAudit) {
      allOptions['comments'] = true;
    }
    setProps(allOptions);
  };

  const hasProp = Object.values(props).some((value) => value === true);
  const allOptionsSelected = !isAudit
    ? otherOptions?.length === selected?.length
    : !!Object.values(props)?.length &&
      Object.values(props).every((value) => value === true);

  const hasSignatures = useMemo(() => {
    const signatures = inspection?.attachments?.filter(
      (a) => a?.extension === 'SIGNATURE',
    );
    return signatures?.length > 0;
  }, [inspection]);

  return (
    <div className={styles.container}>
      <Button
        value={buttonText}
        icon={buttonIcon}
        variant={variant}
        outlined
        disabled={!online}
        className={styles.pdfButton}
        onClick={() => setShowModal(true)}
      />
      <Modal
        open={showModal}
        submitDisabled={!hasProp && isAudit}
        onClose={() => {
          setProps({});
          setShowModal(false);
        }}
        footer={
          !!showCopy ? (
            <div>
              <Form.Check
                type="switch"
                label="Copy Answers"
                checked={copy}
                onChange={(e) => setCopy(e.target.checked)}
              />
            </div>
          ) : null
        }
        title={title}
        onSubmit={() => {
          !isAudit
            ? onSubmit(selected)
            : downloadPDF({
                correctiveActions: correctiveActions,
                audit: inspection,
                props,
                notes,
                fileName,
              });
          setProps({});
          setSelected([]);
        }}
      >
        <div className={styles.spacer} />
        {!isAudit ? (
          <div>
            {!options.length ? (
              <Text noMargin>
                Are you sure you want to copy this assessment?
              </Text>
            ) : (
              <div className={styles.container}>
                <div>
                  {contentOptions?.length ? (
                    <>
                      <Text marginXs weight="semiBold">
                        Select Categories
                      </Text>
                      {contentOptions.map((c) => (
                        <div className={styles.flexRow} key={c.id}>
                          <Form.Check
                            checked={selected.includes(c.id)}
                            onChange={() =>
                              setSelected((prev) =>
                                prev.includes(c.id)
                                  ? prev.filter((itemId) => itemId !== c.id)
                                  : [...prev, c.id],
                              )
                            }
                          />
                          <Text noMargin truncate>
                            {c.title}
                          </Text>
                        </div>
                      ))}
                      <br />
                    </>
                  ) : null}
                </div>
                {!!subtitle && (
                  <Text weight="semiBold" marginSm>
                    {subtitle}
                  </Text>
                )}
                {options?.map((o) => (
                  <div className={styles.flexRow} key={o.id}>
                    <Form.Check
                      checked={!!selected.includes(o.id)}
                      onChange={() =>
                        setSelected((prev) => {
                          if (prev.includes(o.id)) {
                            return prev.filter((itemId) => itemId !== o.id);
                          } else {
                            return [...prev, o.id];
                          }
                        })
                      }
                    />
                    <div>
                      <Text noMargin truncate className={styles.container}>
                        {o.title}
                      </Text>
                      {showDescription && (
                        <Text size="sm" truncate noMargin>
                          {o.description || 'No Description'}
                        </Text>
                      )}
                    </div>

                    {o?.id === 'signatures' &&
                      selected.includes(o.id) &&
                      hasSignatures && (
                        <div className={styles.flexRow}>
                          {' - '}
                          <Form.Check
                            checked={!!selected.includes('signature_export')}
                            onChange={() =>
                              setSelected((prev) => {
                                if (prev.includes('signature_export')) {
                                  return prev.filter(
                                    (itemId) => itemId !== 'signature_export',
                                  );
                                } else {
                                  return [...prev, 'signature_export'];
                                }
                              })
                            }
                          />
                          <Text noMargin truncate className={styles.container}>
                            Export Signatures to Sheet
                          </Text>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          Object.entries(auditReportTypes).map(([key, value]) => (
            <div key={key} className={styles.checkContainer}>
              <div className={styles.check}>
                <Form.Check
                  checked={!!props[key]}
                  onChange={() => handleCheckboxChange(key)}
                />
                <Text noMargin>{value}</Text>
              </div>
              {!!props.checklist && key === 'checklist' && (
                <div className={styles.subCheck}>
                  <Form.Check
                    checked={!!props.comments}
                    onChange={() => handleCheckboxChange('comments')}
                  />
                  <Text noMargin>Comments</Text>
                </div>
              )}
            </div>
          ))
        )}
        <br />
        {(isAudit || !!options?.length) && (
          <Text
            weight="semiBold"
            color="accentPrimary"
            onClick={() => handleSelectAll()}
            hover
          >
            {allOptionsSelected ? 'Deselect All' : 'Select All'}
          </Text>
        )}
        <br />
      </Modal>
    </div>
  );
}
